/**
 * ProductCardView — карточка препарата СЗР (AK-385, экран 1 + 4 вайрфрейма).
 *
 * Шапка (название, форма, состав д.в., статус, регистрант, № госрег.,
 * класс опасности, даты) + таблица регламентов строго по колонкам госкаталога
 * (2–7) + блок аналоги (строгий/широкий, подсветка различий состава/формы).
 *
 * Широкая таблица регламентов скроллит горизонтально ВНУТРИ своего контейнера
 * (overflow-x-auto), не ломая layout страницы (адаптивная вёрстка).
 */
import { useState } from 'react';
import { Plane, Home, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PesticideFreshnessPlaque } from '@/components/pesticides/PesticideFreshnessPlaque';
import { useAnalogs } from '@/lib/pesticides/queries';
import type {
  ProductCard,
  ProductListItem,
  RegulationOut,
} from '@/lib/pesticides/types';
import { fmtRate, StatusBadge, statusLabel, substancesLine } from './parts';

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground">{value || '—'}</div>
    </div>
  );
}

function regTarget(reg: RegulationOut): string {
  return reg.crop_name ?? reg.treated_object_name ?? '—';
}

function harmfulObjectsText(reg: RegulationOut): string {
  if (!reg.harmful_objects.length) return '—';
  return reg.harmful_objects.map((o) => o.name).join(', ');
}

function waitingText(reg: RegulationOut): string {
  const days = reg.waiting_days ?? '—';
  const n = reg.treatments ?? '-';
  return `${days}(${n})`;
}

/** Таблица регламентов — строго по колонкам каталога (2–7). */
function RegulationsTable({ regulations }: { regulations: RegulationOut[] }) {
  if (!regulations.length) {
    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        Регламенты применения не указаны.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[880px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[130px]">Норма (л/га, кг/га)</TableHead>
            <TableHead className="w-[150px]">Культура, объект</TableHead>
            <TableHead className="w-[200px]">Вредный объект</TableHead>
            <TableHead>Способ, время обработки</TableHead>
            <TableHead className="w-[120px]">Срок ожид. (кратн.)</TableHead>
            <TableHead className="w-[110px]">Сроки выхода</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {regulations.map((reg) => (
            <TableRow key={reg.id}>
              <TableCell className="align-top font-medium text-foreground">
                {fmtRate(reg.rate_min, reg.rate_max, reg.rate_unit)}
              </TableCell>
              <TableCell className="align-top text-sm">
                <span className="text-foreground">{regTarget(reg)}</span>
              </TableCell>
              <TableCell className="align-top text-sm text-muted-foreground">
                {harmfulObjectsText(reg)}
              </TableCell>
              <TableCell className="align-top text-sm text-muted-foreground">
                <div className="flex flex-col gap-1">
                  <span>{reg.method_text ?? '—'}</span>
                  {reg.working_fluid && (
                    <span className="text-xs">
                      Рабочая жидкость: {reg.working_fluid}
                    </span>
                  )}
                  {(reg.aviation || reg.personal_plots) && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {reg.aviation && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 rounded-md bg-accent px-1.5 py-0.5 text-[11px] text-accent-foreground">
                              <Plane className="h-3 w-3" /> А
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Авиационное применение</TooltipContent>
                        </Tooltip>
                      )}
                      {reg.personal_plots && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 rounded-md bg-accent px-1.5 py-0.5 text-[11px] text-accent-foreground">
                              <Home className="h-3 w-3" /> Л
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Личные подсобные хозяйства</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="align-top text-sm text-muted-foreground">
                {waitingText(reg)}
              </TableCell>
              <TableCell className="align-top text-sm text-muted-foreground">
                {reg.reentry_days ?? '—(-)'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Блок аналогов — строгий/широкий с подсветкой различий (экран 4). */
function AnalogsBlock({
  product,
  onOpen,
}: {
  product: ProductCard;
  onOpen: (id: string) => void;
}) {
  const [strict, setStrict] = useState(false);
  const analogsQ = useAnalogs(product.id, strict);
  const baseSubs = substancesLine(product.substances);
  const analogs = analogsQ.data ?? [];

  const diffMark = (a: ProductListItem): string | null => {
    const aSubs = substancesLine(a.substances);
    const sameSubs = aSubs === baseSubs;
    const sameForm = (a.formulation ?? '') === (product.formulation ?? '');
    if (!sameSubs) return '≠ состав/конц.';
    if (!sameForm) return '≠ форма';
    return '= состав';
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">
          Аналоги{analogs.length ? ` · ${analogs.length}` : ''}
        </h3>
        <div className="inline-flex overflow-hidden rounded-md border border-border text-xs">
          <button
            type="button"
            onClick={() => setStrict(true)}
            className={
              strict
                ? 'bg-primary px-3 py-1 text-primary-foreground'
                : 'px-3 py-1 text-muted-foreground hover:bg-accent'
            }
          >
            строгий (та же форма)
          </button>
          <button
            type="button"
            onClick={() => setStrict(false)}
            className={
              !strict
                ? 'bg-primary px-3 py-1 text-primary-foreground'
                : 'px-3 py-1 text-muted-foreground hover:bg-accent'
            }
          >
            широкий
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Тот же состав д.в. + пересечение культур · сам препарат исключён.
      </p>

      {analogsQ.isLoading && (
        <div className="mt-3 space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}

      {analogsQ.isError && (
        <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {analogsQ.error?.message ?? 'Не удалось загрузить аналоги'}
        </div>
      )}

      {!analogsQ.isLoading && !analogsQ.isError && analogs.length === 0 && (
        <div className="mt-3 text-sm text-muted-foreground">
          Аналоги не найдены.
        </div>
      )}

      {analogs.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <Table className="min-w-[640px]">
            <TableHeader>
              <TableRow>
                <TableHead>Препарат</TableHead>
                <TableHead className="w-[220px]">Содержание д.в.</TableHead>
                <TableHead className="w-[90px]">Форма</TableHead>
                <TableHead className="w-[160px]">Заявитель</TableHead>
                <TableHead className="w-[100px]">Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analogs.map((a) => {
                const mark = diffMark(a);
                return (
                  <TableRow key={a.id}>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => onOpen(a.id)}
                        className="text-left font-medium text-foreground hover:text-primary"
                      >
                        {a.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {substancesLine(a.substances)}
                      {mark && mark !== '= состав' && (
                        <Badge variant="accent-brand" className="ml-2">
                          {mark}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {a.formulation ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {a.registrant ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {statusLabel(a.status)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function ProductCardSkeleton() {
  return (
    <Card className="p-5">
      <Skeleton className="h-6 w-56" />
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
      <Skeleton className="mt-6 h-40 w-full" />
    </Card>
  );
}

export function ProductCardView({
  product,
  onOpen,
}: {
  product: ProductCard;
  onOpen: (id: string) => void;
}) {
  return (
    <Card className="p-5">
      {/* Шапка */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">
              {product.name}
            </h2>
            {product.formulation && (
              <Badge variant="outline">{product.formulation}</Badge>
            )}
            <StatusBadge status={product.status} />
          </div>
          {product.categories.length > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              {product.categories.join(' · ')}
            </p>
          )}
        </div>
      </div>

      <Separator className="my-4" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="min-w-0 sm:col-span-2 lg:col-span-3">
          <div className="text-xs text-muted-foreground">Содержание д.в.</div>
          <div className="text-sm text-foreground">
            {substancesLine(product.substances)}
          </div>
        </div>
        <HeaderField
          label="Заявитель · ОГРН"
          value={`${product.registrant ?? '—'} · ${product.registrant_ogrn ?? '—'}`}
        />
        <HeaderField
          label="№ гос. регистрации"
          value={product.registration_number}
        />
        <HeaderField
          label="Классы опасности (человек / пчёлы)"
          value={`${product.hazard_human ?? '—'} / ${product.hazard_bees ?? '—'}`}
        />
        <HeaderField
          label="Дата выдачи"
          value={product.date_registered ?? '—'}
        />
        <HeaderField
          label="Дата окончания"
          value={product.date_expires ?? '—'}
        />
        {product.restrictions && (
          <HeaderField label="Ограничения" value={product.restrictions} />
        )}
      </div>

      <Separator className="my-4" />

      <h3 className="mb-2 text-sm font-medium text-foreground">
        Регламенты применения — по госкаталогу (кол. 2–7)
      </h3>
      <div className="rounded-lg border border-border">
        <RegulationsTable regulations={product.regulations} />
      </div>

      <Separator className="my-4" />

      <AnalogsBlock product={product} onOpen={onOpen} />

      {/* Происхождение данных: редакция Госкаталога + дата синка (muted, подвал). */}
      <Separator className="my-4" />
      <PesticideFreshnessPlaque />
    </Card>
  );
}
